import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";

import { useRepQueries } from "../api/repApi";
import OnboardingSession from "../models/onboardingSession";
import { RepStatusEnum } from "../models/rep";
import { useRepContext } from "./editableItemContext";
import { useSegmentContext } from "./segmentContext";

export interface OnboardingContextType {
	sessionId: number;
	setSessionId: (id: number) => void;
	session: OnboardingSession;
}

const defaultContextValues = {
	sessionId: undefined,
	setSessionId: undefined,
	session: undefined,
};

export const OnboardingContext = createContext<OnboardingContextType>(defaultContextValues);

export const useOnboardingContext = () => useContext(OnboardingContext);

interface OnboardingProviderProps {
	children: ReactNode;
}

export function OnboardingProvider(props: OnboardingProviderProps) {
	const { children } = props;

	const { useOnboardingSessionsQuery } = useRepQueries();
	const { savedRep } = useRepContext();
	const { segmentId } = useSegmentContext();
	const { data: obSessions } = useOnboardingSessionsQuery();
	const [sessionId, setSessionId] = useState<number | undefined>();
	const latestSession = useMemo(() => {
		if (!segmentId) return null;
		return obSessions
			?.filter((s) => s.repSegmentIds.includes(segmentId))
			?.sort((a, b) => b.id - a.id)
			.first();
	}, [obSessions, segmentId]);
	useEffect(() => {
		if (!latestSession) setSessionId(undefined);
		else if (savedRep.statusEnum !== RepStatusEnum.Active) setSessionId(latestSession.id);
	}, [latestSession]);
	const session = useMemo(() => obSessions?.get(sessionId), [obSessions, sessionId]);

	return (
		<OnboardingContext.Provider value={{ sessionId, setSessionId, session }}>{children}</OnboardingContext.Provider>
	);
}
